import React, { useState, useEffect, useContext, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { getTrades, getAllPricesDb, getDividends, getCashTransactions, getMutualFundEntries, getNotifications, markNotificationRead, markAllNotificationsRead } from './api';
import { usePortfolioData } from './hooks/usePortfolioData';
import BusinessIcon from '@mui/icons-material/Business';
import { CircularProgress } from '@mui/material';

import { AuthProvider, AuthContext } from './context/AuthContext';
import { PortfolioProvider, PortfolioContext } from './context/PortfolioContext';
import { CustomThemeProvider, useThemeMode } from './context/ThemeContext';
import { Navigate } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Box, CssBaseline,
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton,
  Menu, MenuItem, Avatar, Select, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Divider, Badge, Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import CalculateIcon from '@mui/icons-material/Calculate';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NotificationsIcon from '@mui/icons-material/Notifications';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

import { useLocation, useNavigate } from 'react-router-dom';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const ConsolidatedDashboard = lazy(() => import('./pages/ConsolidatedDashboard'));
const MutualFundDashboard = lazy(() => import('./pages/MutualFundDashboard'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const CashManagement = lazy(() => import('./pages/CashManagement'));
const StockDetails = lazy(() => import('./pages/StockDetails'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const About = lazy(() => import('./pages/About'));
const FeatureDividend = lazy(() => import('./pages/FeatureDividend'));
const FeatureWatchlist = lazy(() => import('./pages/FeatureWatchlist'));
const FeatureAiInsights = lazy(() => import('./pages/FeatureAiInsights'));
const FeaturePortfolio = lazy(() => import('./pages/FeaturePortfolio'));
const FeatureNotifications = lazy(() => import('./pages/FeatureNotifications'));
const ZakatCalculator = lazy(() => import('./pages/ZakatCalculator'));
const SalaryTaxCalculator = lazy(() => import('./pages/SalaryTaxCalculator'));
const CagrCalculator = lazy(() => import('./pages/CagrCalculator'));
const SipCalculator = lazy(() => import('./pages/SipCalculator'));
const PeterLynchCalculator = lazy(() => import('./pages/PeterLynchCalculator'));
const CompanyProfile = lazy(() => import('./pages/CompanyProfile'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AdminCompanies = lazy(() => import('./pages/AdminCompanies'));
const TradeHistory = lazy(() => import('./pages/TradeHistory'));
const DividendAnalytics = lazy(() => import('./pages/DividendAnalytics'));
const TaxGains = lazy(() => import('./pages/TaxGains'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminUserDetail = lazy(() => import('./pages/AdminUserDetail'));
const Watchlist = lazy(() => import('./pages/Watchlist'));
const AiInsights = lazy(() => import('./pages/AiInsights'));
const UpcomingEventsPage = lazy(() => import('./pages/UpcomingEventsPage'));
const FeatureTaxGains = lazy(() => import('./pages/FeatureTaxGains'));
const FeatureUpcomingEvents = lazy(() => import('./pages/FeatureUpcomingEvents'));
const FeatureMultiPortfolio = lazy(() => import('./pages/FeatureMultiPortfolio'));
const FeatureCashLedger = lazy(() => import('./pages/FeatureCashLedger'));

const drawerWidth = 240;

function AppLayout({ trades, setTrades, dbPrices, dividends, cashTransactions, mutualFundEntries, fetchDashboardData }) {
  const { toggleTheme, isDark } = useThemeMode();
  const renderBoldText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} style={{ fontWeight: 800 }}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [newPortfolioOpen, setNewPortfolioOpen] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [newPortfolioType, setNewPortfolioType] = useState('BROKERAGE');
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifPage, setNotifPage] = useState(1);
  const [hasMoreNotifs, setHasMoreNotifs] = useState(true);
  const [isLoadingNotifs, setIsLoadingNotifs] = useState(false);
  const [globalUnreadCount, setGlobalUnreadCount] = useState(0);

  const notifPageRef = React.useRef(1);
  const hasMoreNotifsRef = React.useRef(true);
  const isLoadingNotifsRef = React.useRef(false);

  const unreadNotifs = notifications.filter(n => !n.isRead);
  const readNotifs = notifications.filter(n => n.isRead);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isImpersonating, stopImpersonating } = useContext(AuthContext);
  const { portfolios, activePortfolio, setActivePortfolio, createPortfolio } = useContext(PortfolioContext);
  const { holdings, totals, realizedHistory } = usePortfolioData(trades, dbPrices, dividends, cashTransactions, activePortfolio?.type);
  const isMutualFund = activePortfolio?.type === 'MUTUAL_FUND';

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  
  const handleNotifOpen = (event) => setNotifAnchorEl(event.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handlePortfolioChange = (event) => {
    const selectedId = event.target.value;
    if (selectedId === '__new__') {
      setNewPortfolioOpen(true);
      return;
    }
    if (selectedId === '__manage__') {
      navigate('/settings', { state: { tab: 2 } });
      return;
    }
    if (selectedId === 'all') {
      setActivePortfolio({ _id: 'all', name: 'All Portfolios', type: 'ALL' });
      if (location.pathname === '/dashboard') {
        navigate('/consolidated');
      }
      return;
    }
    const selected = portfolios.find(p => p._id === selectedId);
    if (selected) {
      setActivePortfolio(selected);
      if (selected.type === 'MUTUAL_FUND') {
        if (!['/dashboard', '/portfolio', '/settings'].includes(location.pathname)) {
          navigate('/dashboard');
        }
      } else if (location.pathname === '/consolidated') {
        navigate('/dashboard');
      }
    }
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) return;
    try {
      await createPortfolio({ name: newPortfolioName.trim(), type: newPortfolioType || 'BROKERAGE' });
      setNewPortfolioName('');
      setNewPortfolioType('BROKERAGE');
      setNewPortfolioOpen(false);
    } catch (err) {
      console.error('Failed to create portfolio', err);
    }
  };

  const loadNotifications = async (skip = 0, append = false) => {
    if (isLoadingNotifsRef.current) return;
    setIsLoadingNotifs(true);
    isLoadingNotifsRef.current = true;
    try {
      const res = await getNotifications(skip);
      if (append) {
        setNotifications(prev => [...prev, ...res.data.notifications]);
      } else {
        setNotifications(res.data.notifications);
        // On initial load, sync the global unread count
        if (res.data.unreadCount !== undefined) {
          setGlobalUnreadCount(res.data.unreadCount);
        }
      }
      setHasMoreNotifs(res.data.hasMore);
      hasMoreNotifsRef.current = res.data.hasMore;
      // We don't need notifPageRef anymore, skip is derived from notifications.length
    } catch (err) {
      console.error('Failed to load notifications', err);
    } finally {
      setIsLoadingNotifs(false);
      isLoadingNotifsRef.current = false;
    }
  };

  const handleNotifScroll = (e) => {
    const target = e.currentTarget;
    if (!target) return;
    // Calculate if we are near the bottom (within 100px)
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    
    if (bottom && hasMoreNotifsRef.current && !isLoadingNotifsRef.current) {
      // Use the current length of the notifications array as the offset
      loadNotifications(notifications.length, true);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.isRead) {
        await markNotificationRead(notif._id);
        setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, isRead: true } : n));
        setGlobalUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setGlobalUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read', err);
    }
  };

  useEffect(() => {
    let intervalId;
    if (user && activePortfolio) {
      fetchDashboardData(activePortfolio._id);
      loadNotifications(1, false);

      // Auto-refresh data every 5 minutes (300,000 ms)
      intervalId = setInterval(() => {
        fetchDashboardData(activePortfolio._id);
        loadNotifications(0, false);
      }, 300000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, activePortfolio]); // re-fetch when user or active portfolio changes

  const isPublicRoute = ['/', '/about', '/privacy-policy', '/terms-of-use', '/login', '/register'].includes(location.pathname) || 
                        location.pathname.startsWith('/features/') || 
                        location.pathname.startsWith('/calculators/') || 
                        location.pathname.startsWith('/company/');
  const showAuthLayout = user && !isPublicRoute;

  return (
    <>
      {isImpersonating && (
        <Box sx={{ position: 'fixed', bottom: 0, width: '100%', zIndex: 9999, bgcolor: '#F59E0B', color: 'white', p: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, boxShadow: '0 -4px 10px rgba(0,0,0,0.1)' }}>
          <Typography variant="body1" fontWeight="bold">You are currently impersonating {user?.firstName} {user?.lastName}.</Typography>
          <Button size="small" variant="contained" sx={{ bgcolor: 'white', color: '#F59E0B', '&:hover': { bgcolor: '#f0f0f0' } }} onClick={stopImpersonating}>Return to Admin</Button>
        </Box>
      )}
      <Box sx={{ display: 'flex' }}>
        {showAuthLayout && (
        <>
          <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="toggle drawer"
                onClick={handleDrawerToggle}
                edge="start"
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, sm: 3 } }}>
                <img src="/favicon.svg" alt="PSX Logo" style={{ width: 28, height: 28, marginRight: 12 }} />
                <Typography variant="h6" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  PSX Portfolio Tracker
                </Typography>
              </Box>

              {/* Portfolio Switcher */}
              {portfolios.length > 0 && (
                <Select
                  value={activePortfolio ? activePortfolio._id : ''}
                  onChange={handlePortfolioChange}
                  size="small"
                  sx={{
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                    '.MuiSvgIcon-root': { color: 'white' },
                    minWidth: { xs: 130, sm: 190 },
                    maxWidth: { xs: 170, sm: 'none' },
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}
                >
                  <MenuItem value="all" sx={{ fontWeight: 700, color: 'primary.main', bgcolor: '#EEF2FF' }}>
                    <LayersIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    🌟 All Portfolios
                  </MenuItem>
                  <Divider />
                  {portfolios.map(p => (
                    <MenuItem key={p._id} value={p._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                        <span>{p.name}</span>
                        {p.type === 'CUSTODY_CDC' && (
                          <Typography component="span" sx={{ fontSize: '0.68rem', bgcolor: '#F3E8FF', color: '#7E22CE', px: 0.7, py: 0.2, borderRadius: 1, fontWeight: 700 }}>
                            CDC
                          </Typography>
                        )}
                        {p.type === 'MUTUAL_FUND' && (
                          <Typography component="span" sx={{ fontSize: '0.68rem', bgcolor: '#DCFCE7', color: '#15803D', px: 0.7, py: 0.2, borderRadius: 1, fontWeight: 700 }}>
                            AMC / MF
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="__new__">
                    <AddIcon fontSize="small" sx={{ mr: 1 }} />
                    Add New Portfolio
                  </MenuItem>
                  <MenuItem value="__manage__">
                    <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                    Manage Portfolios
                  </MenuItem>
                </Select>
              )}

              <Box sx={{ flexGrow: 1 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Financial Dark / Light Theme Switch */}
                <Tooltip title={isDark ? "Switch to Light Theme" : "Switch to Financial Dark Theme"}>
                  <IconButton
                    color="inherit"
                    onClick={toggleTheme}
                    size="small"
                    sx={{
                      mr: { xs: 0.5, sm: 1.5 },
                      p: 0.8,
                      border: '1px solid',
                      borderColor: 'rgba(255, 255, 255, 0.25)',
                      borderRadius: 2,
                      bgcolor: isDark ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                    aria-label="Toggle financial theme"
                  >
                    {isDark ? (
                      <LightModeIcon sx={{ fontSize: 20, color: '#FBBF24' }} />
                    ) : (
                      <DarkModeIcon sx={{ fontSize: 20, color: '#38BDF8' }} />
                    )}
                  </IconButton>
                </Tooltip>

                <IconButton color="inherit" onClick={handleNotifOpen} sx={{ mr: { xs: 0, sm: 2 } }}>
                  <Badge badgeContent={globalUnreadCount} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                
                {/* Notifications Menu */}
                <Menu
                  anchorEl={notifAnchorEl}
                  open={Boolean(notifAnchorEl)}
                  onClose={handleNotifClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  MenuListProps={{
                    sx: { p: 0, display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'hidden',
                      filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.15))',
                      mt: 1.5,
                      width: 360,
                      minWidth: 360,
                      maxWidth: 360,
                      maxHeight: 500,
                      display: 'flex',
                      flexDirection: 'column'
                    },
                  }}
                >
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', bgcolor: isDark ? 'background.default' : '#F8FAFC' }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', color: 'text.primary', fontWeight: 700 }}>Notifications</Typography>
                    {notifications.some(n => !n.isRead) && (
                      <Button size="small" onClick={handleMarkAllRead} sx={{ fontSize: '0.75rem' }}>Mark all as read</Button>
                    )}
                  </Box>
                  <Box sx={{ overflowY: 'auto', flexGrow: 1, maxHeight: 500 }} onScroll={handleNotifScroll}>
                    {notifications.length === 0 && !isLoadingNotifs ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">No notifications yet.</Typography>
                      </Box>
                    ) : (
                      <>
                        {unreadNotifs.length > 0 && (
                          <Typography variant="overline" sx={{ px: 2, pt: 1, pb: 0.5, color: isDark ? '#38BDF8' : '#64748B', display: 'block', bgcolor: isDark ? 'rgba(56, 189, 248, 0.08)' : '#F8FAFC', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                            New
                          </Typography>
                        )}
                        {unreadNotifs.map(notif => (
                          <MenuItem 
                            key={notif._id} 
                            onClick={() => handleNotificationClick(notif)}
                            sx={{ 
                              px: 2, 
                              py: 1.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              bgcolor: isDark ? 'rgba(56, 189, 248, 0.08)' : '#EEF2FF',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              whiteSpace: 'normal',
                              '&:hover': { bgcolor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#E0E7FF' }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {renderBoldText(notif.title)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 60, textAlign: 'right', pt: 0.3 }}>
                                {new Date(notif.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
                              {renderBoldText(notif.message)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 0.5 }}>
                              {notif.type === 'REPORT' && notif.link ? (
                                <Typography 
                                  variant="caption" 
                                  onClick={(e) => { e.stopPropagation(); window.open(notif.link, '_blank'); }}
                                  sx={{ 
                                    color: isDark ? '#38BDF8' : '#4F46E5', 
                                    fontWeight: 600, 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  Open Report <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Typography>
                              ) : <Box />}
                              <Typography 
                                variant="caption" 
                                onClick={(e) => { e.stopPropagation(); handleNotificationClick(notif); }}
                                sx={{ 
                                  color: isDark ? '#38BDF8' : '#4F46E5', 
                                  fontWeight: 600, 
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                Mark as read
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}

                        {readNotifs.length > 0 && (
                          <Typography variant="overline" sx={{ px: 2, pt: 1, pb: 0.5, color: 'text.secondary', display: 'block', bgcolor: isDark ? 'background.default' : '#F8FAFC', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                            Earlier
                          </Typography>
                        )}
                        {readNotifs.map(notif => (
                          <MenuItem 
                            key={notif._id} 
                            onClick={() => handleNotificationClick(notif)}
                            sx={{ 
                              px: 2, 
                              py: 1.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              bgcolor: 'transparent',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              whiteSpace: 'normal',
                              '&:hover': { bgcolor: isDark ? 'action.hover' : '#F8FAFC' }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {renderBoldText(notif.title)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 60, textAlign: 'right', pt: 0.3 }}>
                                {new Date(notif.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
                              {renderBoldText(notif.message)}
                            </Typography>
                            {notif.type === 'REPORT' && notif.link && (
                              <Box sx={{ display: 'flex', width: '100%', mt: 0.5 }}>
                                <Typography 
                                  variant="caption" 
                                  onClick={(e) => { e.stopPropagation(); window.open(notif.link, '_blank'); }}
                                  sx={{ 
                                    color: isDark ? '#38BDF8' : '#4F46E5', 
                                    fontWeight: 600, 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  Open Report <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Typography>
                              </Box>
                            )}
                          </MenuItem>
                        ))}
                      </>
                    )}
                    {isLoadingNotifs && (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </Box>
                </Menu>

                <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                  Welcome, {user.firstName}
                </Typography>
                <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '0.9rem' }}>
                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                      mt: 1.5,
                      minWidth: 150,
                    },
                  }}
                >
                  <MenuItem component={Link} to="/settings" state={{ tab: 0 }} onClick={handleMenuClose}>
                    <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                    Profile
                  </MenuItem>
                  <MenuItem component={Link} to="/settings" state={{ tab: 1 }} onClick={handleMenuClose}>
                    <ListItemIcon><SecurityIcon fontSize="small" /></ListItemIcon>
                    Security
                  </MenuItem>
                  <MenuItem component={Link} to="/settings" state={{ tab: 2 }} onClick={handleMenuClose}>
                    <ListItemIcon><AccountBalanceWalletIcon fontSize="small" /></ListItemIcon>
                    Portfolios
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </AppBar>
          
          <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
            {/* Mobile Drawer */}
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}
            >
              <Toolbar />
              <Box sx={{ overflow: 'auto', pt: 2 }} onClick={handleDrawerToggle}>
                <List>
                  {activePortfolio?._id !== 'all' && (
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/dashboard" selected={location.pathname === '/dashboard'}>
                      <ListItemIcon>
                        <DashboardIcon />
                      </ListItemIcon>
                      <ListItemText primary="Dashboard" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  )}
                  {activePortfolio?._id === 'all' && (
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/consolidated" selected={location.pathname === '/consolidated'}>
                      <ListItemIcon>
                        <LayersIcon />
                      </ListItemIcon>
                      <ListItemText primary="Consolidated Dashboard" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/portfolio" selected={location.pathname === '/portfolio'}>
                      <ListItemIcon>
                        <ShowChartIcon />
                      </ListItemIcon>
                      <ListItemText primary="Portfolio" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  {!isMutualFund && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/history" selected={location.pathname === '/history'}>
                          <ListItemIcon>
                            <HistoryIcon />
                          </ListItemIcon>
                          <ListItemText primary="Transactions" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/dividends" selected={location.pathname === '/dividends'}>
                          <ListItemIcon>
                            <AccountBalanceWalletIcon />
                          </ListItemIcon>
                          <ListItemText primary="Dividends" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/upcoming-events" selected={location.pathname === '/upcoming-events'}>
                          <ListItemIcon>
                            <EventIcon />
                          </ListItemIcon>
                          <ListItemText primary="Upcoming Events" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/tax-gains" selected={location.pathname === '/tax-gains'}>
                          <ListItemIcon>
                            <CalculateIcon />
                          </ListItemIcon>
                          <ListItemText primary="Tax & Gains" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/cash" selected={location.pathname === '/cash'}>
                          <ListItemIcon>
                            <AccountBalanceWalletIcon />
                          </ListItemIcon>
                          <ListItemText primary="Cash" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/watchlist" selected={location.pathname === '/watchlist'}>
                          <ListItemIcon>
                            <VisibilityIcon />
                          </ListItemIcon>
                          <ListItemText primary="Watchlist" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/ai-insights" selected={location.pathname === '/ai-insights'}>
                          <ListItemIcon>
                            <AutoAwesomeIcon />
                          </ListItemIcon>
                          <ListItemText primary="AI Insights" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                    </>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/settings" selected={location.pathname === '/settings'}>
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Settings" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  {!isMutualFund && user?.role === 'admin' && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/admin" selected={location.pathname === '/admin'}>
                          <ListItemIcon>
                            <SupervisorAccountIcon />
                          </ListItemIcon>
                          <ListItemText primary="Admin Panel" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/admin/companies" selected={location.pathname === '/admin/companies'}>
                          <ListItemIcon>
                            <BusinessIcon />
                          </ListItemIcon>
                          <ListItemText primary="Companies" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                    </>
                  )}
                </List>
              </Box>
            </Drawer>

            {/* Desktop Drawer */}
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', sm: 'block' },
                [`& .MuiDrawer-paper`]: { 
                  width: drawerWidth, 
                  boxSizing: 'border-box',
                  borderRight: '1px solid #E2E8F0'
                },
              }}
              open
            >
              <Toolbar />
              <Box sx={{ overflow: 'auto', pt: 2 }}>
                <List>
                  {activePortfolio?._id !== 'all' && (
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/dashboard" selected={location.pathname === '/dashboard'}>
                      <ListItemIcon>
                        <DashboardIcon />
                      </ListItemIcon>
                      <ListItemText primary="Dashboard" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  )}
                  {activePortfolio?._id === 'all' && (
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/consolidated" selected={location.pathname === '/consolidated'}>
                      <ListItemIcon>
                        <LayersIcon />
                      </ListItemIcon>
                      <ListItemText primary="Consolidated Dashboard" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/portfolio" selected={location.pathname === '/portfolio'}>
                      <ListItemIcon>
                        <ShowChartIcon />
                      </ListItemIcon>
                      <ListItemText primary="Portfolio" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  {!isMutualFund && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/history" selected={location.pathname === '/history'}>
                          <ListItemIcon>
                            <HistoryIcon />
                          </ListItemIcon>
                          <ListItemText primary="Transactions" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/dividends" selected={location.pathname === '/dividends'}>
                          <ListItemIcon>
                            <AccountBalanceWalletIcon />
                          </ListItemIcon>
                          <ListItemText primary="Dividends" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/upcoming-events" selected={location.pathname === '/upcoming-events'}>
                          <ListItemIcon>
                            <EventIcon />
                          </ListItemIcon>
                          <ListItemText primary="Upcoming Events" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/tax-gains" selected={location.pathname === '/tax-gains'}>
                          <ListItemIcon>
                            <CalculateIcon />
                          </ListItemIcon>
                          <ListItemText primary="Tax & Gains" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/cash" selected={location.pathname === '/cash'}>
                          <ListItemIcon>
                            <AccountBalanceWalletIcon />
                          </ListItemIcon>
                          <ListItemText primary="Cash" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/watchlist" selected={location.pathname === '/watchlist'}>
                          <ListItemIcon>
                            <VisibilityIcon />
                          </ListItemIcon>
                          <ListItemText primary="Watchlist" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/ai-insights" selected={location.pathname === '/ai-insights'}>
                          <ListItemIcon>
                            <AutoAwesomeIcon />
                          </ListItemIcon>
                          <ListItemText primary="AI Insights" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                    </>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/settings" selected={location.pathname === '/settings'}>
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Settings" primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItemButton>
                  </ListItem>
                  {!isMutualFund && user?.role === 'admin' && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/admin" selected={location.pathname === '/admin'}>
                          <ListItemIcon>
                            <SupervisorAccountIcon />
                          </ListItemIcon>
                          <ListItemText primary="Admin Panel" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/admin/companies" selected={location.pathname === '/admin/companies'}>
                          <ListItemIcon>
                            <BusinessIcon />
                          </ListItemIcon>
                          <ListItemText primary="Companies" primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                    </>
                  )}
                </List>
              </Box>
            </Drawer>
          </Box>
        </>
      )}
      
      <Box component="main" sx={{ flexGrow: 1, p: showAuthLayout ? { xs: 2, sm: 3 } : 0, width: showAuthLayout ? { sm: `calc(100% - ${drawerWidth}px)` } : '100%', backgroundColor: 'background.default', minHeight: '100vh', overflowX: 'hidden' }}>
        {showAuthLayout && <Toolbar />}
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
          <Routes>
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/about" element={<About />} />
          <Route path="/features/portfolio-tracking" element={<FeaturePortfolio />} />
          <Route path="/features/dividend-tracker" element={<FeatureDividend />} />
          <Route path="/features/custom-watchlists" element={<FeatureWatchlist />} />
          <Route path="/features/ai-stock-insights" element={<FeatureAiInsights />} />
          <Route path="/features/real-time-notifications" element={<FeatureNotifications />} />
          <Route path="/features/cgt-tax-calculator" element={<FeatureTaxGains />} />
          <Route path="/features/upcoming-events-tracker" element={<FeatureUpcomingEvents />} />
          <Route path="/features/multi-portfolio-manager" element={<FeatureMultiPortfolio />} />
          <Route path="/features/broker-cash-ledger" element={<FeatureCashLedger />} />
          <Route path="/calculators/zakat" element={<ZakatCalculator />} />
          <Route path="/calculators/salary-tax" element={<SalaryTaxCalculator />} />
          <Route path="/calculators/cagr" element={<CagrCalculator />} />
          <Route path="/calculators/sip" element={<SipCalculator />} />
          <Route path="/calculators/peter-lynch" element={<PeterLynchCalculator />} />
          
          <Route path="/company/:symbol" element={<CompanyProfile />} />

          <Route 
            path="/" 
            element={!user ? <LandingPage /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/dashboard" 
            element={user ? (
              activePortfolio?._id === 'all' ? (
                <Navigate to="/consolidated" replace />
              ) : activePortfolio ? (
                activePortfolio.type === 'MUTUAL_FUND' ? (
                  <MutualFundDashboard
                    activePortfolio={activePortfolio}
                    entries={mutualFundEntries}
                    onRefresh={() => fetchDashboardData(activePortfolio._id)}
                  />
                ) : (
                  <Dashboard totals={totals} holdings={holdings} realizedHistory={realizedHistory} activePortfolioId={activePortfolio?._id} cashTransactions={cashTransactions} fetchDashboardData={() => fetchDashboardData(activePortfolio?._id)} trades={trades} dividends={dividends} />
                )
              ) : (
                <NoPortfolioPrompt onOpen={() => setNewPortfolioOpen(true)} />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/consolidated" 
            element={user ? (
              activePortfolio?._id !== 'all' ? (
                <Navigate to="/dashboard" replace />
              ) : portfolios.length > 0 ? (
                <ConsolidatedDashboard dbPrices={dbPrices} />
              ) : (
                <NoPortfolioPrompt onOpen={() => setNewPortfolioOpen(true)} />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/settings" 
            element={user ? <Settings /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/portfolio" 
            element={user ? (
              activePortfolio ? (
                activePortfolio.type === 'MUTUAL_FUND' ? (
                  <MutualFundDashboard
                    activePortfolio={activePortfolio}
                    entries={mutualFundEntries}
                    onRefresh={() => fetchDashboardData(activePortfolio._id)}
                  />
                ) : (
                  <Portfolio 
                    trades={trades} 
                    setTrades={setTrades} 
                    holdings={holdings} 
                    totals={totals}
                    cashBalance={totals.cashBalance || 0}
                    fetchDashboardData={() => fetchDashboardData(activePortfolio._id)} 
                    activePortfolioId={activePortfolio._id}
                    dividends={dividends}
                    realizedHistory={realizedHistory}
                  />
                )
              ) : (
                <NoPortfolioPrompt onOpen={() => setNewPortfolioOpen(true)} />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/history" 
            element={user ? (
              activePortfolio ? (
                activePortfolio.type === 'MUTUAL_FUND' ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <TradeHistory 
                    trades={trades} 
                    dividends={dividends}
                    fetchDashboardData={() => fetchDashboardData(activePortfolio._id)} 
                  />
                )
              ) : (
                <NoPortfolioPrompt onOpen={() => setNewPortfolioOpen(true)} />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/dividends" 
            element={user ? (
              activePortfolio ? (
                activePortfolio.type === 'MUTUAL_FUND' ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <DividendAnalytics 
                    dividends={dividends} 
                  />
                )
              ) : (
                <NoPortfolioPrompt onOpen={() => setNewPortfolioOpen(true)} />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/upcoming-events" 
            element={user ? (
              activePortfolio?.type === 'MUTUAL_FUND' ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <UpcomingEventsPage />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/tax-gains" 
            element={user ? (
              activePortfolio ? (
                activePortfolio.type === 'MUTUAL_FUND' ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <TaxGains 
                    realizedHistory={realizedHistory} 
                  />
                )
              ) : (
                <NoPortfolioPrompt onOpen={() => setNewPortfolioOpen(true)} />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/cash" 
            element={user ? (
              activePortfolio ? (
                activePortfolio.type === 'MUTUAL_FUND' ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <CashManagement 
                    cashTransactions={cashTransactions} 
                    cashBalance={totals.cashBalance || 0}
                    fetchDashboardData={() => fetchDashboardData(activePortfolio._id)} 
                    activePortfolioId={activePortfolio._id}
                    activePortfolio={activePortfolio}
                  />
                )
              ) : (
                <NoPortfolioPrompt onOpen={() => setNewPortfolioOpen(true)} />
              )
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/watchlist" 
            element={user ? <Watchlist /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/ai-insights" 
            element={user ? <AiInsights /> : <Navigate to="/login" />} 
          />
          <Route
            path="/stock/:symbol"
            element={user ? <StockDetails holdings={holdings} trades={trades} realizedHistory={realizedHistory} dividends={dividends} cashBalance={totals.cashBalance || 0} fetchDashboardData={() => fetchDashboardData(activePortfolio?._id)} activePortfolioId={activePortfolio?._id} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/admin" 
            element={user?.role === 'admin' ? <AdminPanel /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/admin/users/:userId" 
            element={user?.role === 'admin' ? <AdminUserDetail /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/admin/companies" 
            element={user?.role === 'admin' ? <AdminCompanies /> : <Navigate to="/dashboard" />} 
          />
        </Routes>
        </Suspense>
      </Box>

      {/* Create Portfolio Dialog */}
      <Dialog open={newPortfolioOpen} onClose={() => setNewPortfolioOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create New Portfolio</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              autoFocus
              label="Portfolio Name"
              fullWidth
              value={newPortfolioName}
              onChange={(e) => setNewPortfolioName(e.target.value)}
              placeholder="e.g. CDC Direct Account, Tech Portfolio"
              onKeyPress={(e) => { if (e.key === 'Enter') handleCreatePortfolio(); }}
            />
            <TextField
              select
              fullWidth
              label="Account Type"
              value={newPortfolioType}
              onChange={(e) => setNewPortfolioType(e.target.value)}
              helperText={
                newPortfolioType === 'MUTUAL_FUND'
                  ? 'Mutual Fund (AMC) Account: Record your daily total AMC balance across funds. Automatically calculates Daily, Monthly (MTD), FYTD, and Overall P/L in amount & %.'
                  : newPortfolioType === 'CUSTODY_CDC'
                  ? 'CDC Investor Account: Pure stock custody. Tracks current stock holdings without deducting broker cash or creating cash deficits.'
                  : 'Brokerage Trading Account: Standard account with full cash ledger and margin tracking.'
              }
            >
              <MenuItem value="BROKERAGE">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Brokerage Trading Account</Typography>
                  <Typography variant="caption" color="text.secondary">Includes Cash Ledger & Buying Power</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="CUSTODY_CDC">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#7E22CE' }}>CDC Investor Account (Custody)</Typography>
                  <Typography variant="caption" color="text.secondary">Pure Stock Custody (No Cash Ledger / Margin)</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="MUTUAL_FUND">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>Mutual Fund (AMC) Account</Typography>
                  <Typography variant="caption" color="text.secondary">Daily Total Balance Tracker (Monthly, FYTD & Overall P/L)</Typography>
                </Box>
              </MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setNewPortfolioOpen(false); setNewPortfolioType('BROKERAGE'); }}>Cancel</Button>
          <Button onClick={handleCreatePortfolio} variant="contained" color="secondary" sx={{ color: 'white' }} disabled={!newPortfolioName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
}

// Component shown when user has no portfolios yet
function NoPortfolioPrompt({ onOpen }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
      <AccountBalanceWalletIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Welcome! Let's get started.
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4, maxWidth: 400 }}>
        Create your first portfolio to start tracking your PSX investments, trades, and dividends.
      </Typography>
      <Button variant="contained" color="secondary" size="large" sx={{ color: 'white', px: 4 }} onClick={onOpen} startIcon={<AddIcon />}>
        Create Your First Portfolio
      </Button>
    </Box>
  );
}

function App() {
  const [trades, setTrades] = useState([]);
  const [dbPrices, setDbPrices] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [cashTransactions, setCashTransactions] = useState([]);
  const [mutualFundEntries, setMutualFundEntries] = useState([]);

  const fetchDashboardData = async (portfolioId) => {
    if (!portfolioId) return;
    try {
      const [tradesRes, pricesRes, dividendsRes, cashRes, mfRes] = await Promise.all([
        getTrades(portfolioId),
        getAllPricesDb(),
        getDividends(portfolioId),
        getCashTransactions(portfolioId),
        getMutualFundEntries(portfolioId)
      ]);
      setTrades(tradesRes.data);
      setDbPrices(pricesRes.data);
      setDividends(dividendsRes.data);
      setCashTransactions(cashRes.data);
      setMutualFundEntries(mfRes.data || []);
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
    }
  };

  return (
    <CustomThemeProvider>
      <CssBaseline />
      <AuthProvider>
        <PortfolioProvider>
          <Router>
            <AppLayout 
              trades={trades} 
              setTrades={setTrades} 
              dbPrices={dbPrices} 
              dividends={dividends}
              cashTransactions={cashTransactions}
              mutualFundEntries={mutualFundEntries}
              fetchDashboardData={fetchDashboardData} 
            />
          </Router>
        </PortfolioProvider>
      </AuthProvider>
    </CustomThemeProvider>
  );
}

export default App;
